import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Navigation.css';
import logo from '../logo 29.05.png';
import closeIcon from '../close_24dp_1F1F1F.svg';
import expandMoreIcon from '../expand_more_24dp_1F1F1F.svg';
import expandLessIcon from '../expand_less_24dp_1F1F1F.svg';

const Navigation = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
        // Restore body scroll on resize
        document.body.style.overflow = 'unset';
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Cleanup function to restore body scroll when component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = 'unset';
    };
  }, []);

  const categories = {
    'zacece': {
      name: 'Začeće',
      subcategories: {
        'pre-trudnoce': 'Pre trudnoće i planiranje',
        'saveti': 'Saveti'
      }
    },
    'trudnoca': {
      name: 'Trudnoća',
      subcategories: {
        'prvi-trimestar': 'Prvi trimestar',
        'drugi-trimestar': 'Drugi trimestar',
        'prva-kupovina': 'Prva kupovina',
        'treci-trimestar': 'Treći trimestar'
      }
    },
    'porodjaj': {
      name: 'Porođaj',
      subcategories: {
        'priprema-za-porodjaj': 'Priprema za porođaj',
        'priprema-za-postpartum': 'Priprema za postpartum',
        'dojenje': 'Dojenje'
      }
    },
    'zivot-sa-bebom': {
      name: 'Život sa bebom',
      subcategories: {
        'prvo-putovanje': 'Prvo putovanje',
        'nemlecna-ishrana': 'Nemlečna ishrana',
        'dijagnoze-fizijatra': 'Dijagnoze fizijatra',
        'prva-temperatura-korona': 'Prva temperatura i korona',
        'produzenje-porodiljskog': 'Produženje porodiljskog',
        'prvi-rodjendan-krstenje': 'Prvi rođendan i krštenje',
        'hodanje': 'Prohodavanje',
        'psihoza': 'Psihoza',
        'vrtic-adaptacija': 'Vrtić i adaptacija'
      }
    }
  };

  const handleMouseEnter = (category) => {
    if (window.innerWidth > 768) {
      setActiveDropdown(category);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      setActiveDropdown(null);
    }
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    
    // Prevent body scroll when mobile menu is open
    if (newState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  const toggleMobileDropdown = (category) => {
    setActiveDropdown(activeDropdown === category ? null : category);
  };

  return (
    <nav className="main-navigation">
      <div className="nav-content">
        <div className="nav-header">
          {/* Mobile logo and hamburger button */}
          <Link to="/" className="logo-link mobile-logo" onClick={closeMobileMenu}>
            <img src={logo} alt="Jaka kao Majka" className="logo-image" />
          </Link>
          
          {/* Hamburger Menu Button */}
          <button 
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <img src={closeIcon} alt="Close" className="close-icon" />
            ) : (
              <>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
                <span className="hamburger-line"></span>
              </>
            )}
          </button>
        </div>

        <div className="nav-left-group">
          {/* Desktop logo */}
          <Link to="/" className="logo-link desktop-logo">
            <img src={logo} alt="Jaka kao Majka" className="logo-image" />
          </Link>
          
          <ul className="nav-list nav-main-categories">
            {/* Main category items with dropdowns - now non-clickable */}
            {Object.entries(categories).map(([key, category]) => (
              <li
                key={key}
                className="nav-item"
                onMouseEnter={() => handleMouseEnter(key)}
                onMouseLeave={handleMouseLeave}
              >
                <span className="nav-link nav-category-title">
                  {category.name}
                </span>
                {activeDropdown === key && (
                  <ul className="dropdown-menu">
                    {Object.entries(category.subcategories).map(([subKey, subName]) => (
                      <li key={subKey} className="dropdown-item">
                        <Link to={`/category/${key}/${subKey}`}>
                          {subName}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
        
        <ul className="nav-list nav-static-pages">
          <li className="nav-item">
            <Link to="/pravila-koriscenja" className="nav-link">
              Pravila korišćenja
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/o-meni" className="nav-link">
              O meni
            </Link>
          </li>
        </ul>

        {/* Mobile Navigation */}
        <div className={`mobile-navigation ${isMobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-list">
            {/* Main category items for mobile */}
            {Object.entries(categories).map(([key, category]) => (
              <li key={key} className="mobile-nav-item">
                <button 
                  className="mobile-category-button"
                  onClick={() => toggleMobileDropdown(key)}
                >
                  {category.name}
                  <img 
                    src={activeDropdown === key ? expandLessIcon : expandMoreIcon} 
                    alt={activeDropdown === key ? "Collapse" : "Expand"} 
                    className="dropdown-arrow-icon"
                  />
                </button>
                {activeDropdown === key && (
                  <ul className="mobile-dropdown-menu">
                    {Object.entries(category.subcategories).map(([subKey, subName]) => (
                      <li key={subKey} className="mobile-dropdown-item">
                        <Link 
                          to={`/category/${key}/${subKey}`}
                          onClick={closeMobileMenu}
                        >
                          {subName}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            
            {/* Static pages for mobile */}
            <li className="mobile-nav-item">
              <Link 
                to="/pravila-koriscenja" 
                className="mobile-nav-link"
                onClick={closeMobileMenu}
              >
                Pravila korišćenja
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link 
                to="/o-meni" 
                className="mobile-nav-link"
                onClick={closeMobileMenu}
              >
                O meni
              </Link>
            </li>
          </ul>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
        )}
      </div>
    </nav>
  );
};

export default Navigation; 